import { FC, Suspense } from 'react';
import { useFragment, graphql, useLazyLoadQuery } from 'react-relay';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { PhotoRootQuery } from '../interiorsPhoto/PhotoRootQuery';
import InteriorPhotoWrapper from '../interiorsPhoto/InteriorPhotoWrapper';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary';
import styles from './ProjectModalQueryRenderer.scss';

import type { PhotoRootQuery as PhotoRootQueryType } from '../interiorsPhoto/__generated__/PhotoRootQuery.graphql';
import type {
    ProjectModalQueryRenderer_searchResult$key,
    ProjectModalQueryRenderer_searchResult$data,
} from './__generated__/ProjectModalQueryRenderer_searchResult.graphql';

type Props = {
    photoPath: string;
    onCloseModal: () => void;
    searchResult: ProjectModalQueryRenderer_searchResult$key | null;
    userId: string;
};

const ProjectModalWrapper: FC<{
    photoPath: string;
    onCloseModal: () => void;
    searchResult: ProjectModalQueryRenderer_searchResult$data | null;
    userId: string;
}> = ({ searchResult, userId, photoPath, onCloseModal }) => {
    const photos = (searchResult?.project?.tradePhotos || []).filter(filterNulls);
    const photoUrls = photos.map(photo => photo?.url).filter(filterNulls);

    const data = useLazyLoadQuery<PhotoRootQueryType>(PhotoRootQuery, {
        urlLabels: photoUrls || [],
        userId,
        hasUserId: !!userId,
    });
    return (
        <InteriorPhotoWrapper
            isModal
            onCloseModal={onCloseModal}
            viewer={data.viewer}
            initialLookupUrl={photoPath}
            prefetchedPhotos={photos}
        />
    );
};

export const ProjectModalQueryRenderer: FC<Props> = ({
    photoPath,
    onCloseModal,
    searchResult: searchResultRef,
    userId,
}: Props) => {
    const searchResult = useFragment(
        graphql`
            fragment ProjectModalQueryRenderer_searchResult on TradeInteriorProjectQueryOutput {
                project {
                    tradePhotos {
                        url
                        serviceId
                        path
                        width
                        height
                        name
                    }
                }
            }
        `,
        searchResultRef
    );

    return (
        <ErrorBoundary
            fallback={({ error }) => {
                // eslint-disable-next-line no-console
                console.error(error);
                // as a fallback, nav to the page and show an error there
                window.location.href = handleLocaleUrl(photoPath, GLOBAL_CLIENT_ONLY_LOCALE);
                return null;
            }}
        >
            <Suspense
                fallback={
                    <div className={styles.spinnerWrapper}>
                        <Spinner />
                    </div>
                }
            >
                <ProjectModalWrapper
                    searchResult={searchResult}
                    userId={userId}
                    onCloseModal={onCloseModal}
                    photoPath={photoPath}
                />
            </Suspense>
        </ErrorBoundary>
    );
};
